import type { Dispatch, SetStateAction } from "react";
import chatAgentService from "../../services/RealChatAgentService";
import { getFriendlyToolName } from "../../utils/toolNameMapping";
import type { Message, StreamChunk, ToolCall } from "../../types/chat.types";

/**
 * TRAC-497 (useChatStream slice 2): the tool-lifecycle chunk handlers extracted from
 * ChatInterface's streaming loop. tool_use_start appends a running tool part;
 * tool_progress / tool_use_complete / tool_error patch the matching tool (by id OR
 * tool name — the id can differ between start and completion events) in BOTH
 * msg.parts and msg.toolCalls, exactly as the original inline branches did.
 *
 * Side effects that are message-state adjacent live here (persist, TRAC-203
 * stream-state sequence capture); shell-owned side effects (analytics postMessage)
 * come in via opts.onToolComplete.
 */

interface DispatchToolOpts {
  /** Fired on tool_use_complete with the tool name (shell analytics hook). */
  onToolComplete?: (toolName: string) => void;
}

/** id-or-name match: completion events sometimes carry a different id than start. */
const matchesTool = (tool: ToolCall, chunk: StreamChunk): boolean =>
  tool.id === chunk.toolId || tool.tool_name === chunk.toolName;

/** Patch the matching tool in both msg.parts and msg.toolCalls (immutably). */
function patchTool(msg: Message, chunk: StreamChunk, patch: Partial<ToolCall>): Message {
  const parts = (msg.parts || []).map((part) => {
    if (part.type === "tool" && part.toolCall && matchesTool(part.toolCall, chunk)) {
      return { ...part, toolCall: { ...part.toolCall, ...patch } };
    }
    return part;
  });
  return {
    ...msg,
    parts,
    toolCalls: msg.toolCalls?.map((tool) => (matchesTool(tool, chunk) ? { ...tool, ...patch } : tool)),
  };
}

/**
 * Handle a tool-lifecycle chunk for the streaming message `messageId`.
 * Returns true if the chunk type was handled here; false = not a tool chunk.
 */
export function dispatchToolChunk(
  chunk: StreamChunk,
  messageId: string,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  opts?: DispatchToolOpts,
): boolean {
  if (chunk.type === "tool_use_start") {
    setMessages((prev) => {
      const updatedMessages = prev.map((msg) => {
        if (msg.id === messageId) {
          const newToolCall: ToolCall = {
            id: chunk.toolId || Date.now().toString(),
            tool_name: chunk.toolName || "unknown",
            parameters: chunk.parameters,
            status: "running",
            timestamp: new Date(),
            description: getFriendlyToolName(chunk.toolName) || "Processing",
          };
          // Create a new parts array to maintain immutability
          const parts = [...(msg.parts || [])];
          parts.push({ type: "tool", toolCall: newToolCall });
          return { ...msg, toolCalls: [...(msg.toolCalls || []), newToolCall], parts };
        }
        return msg;
      });
      chatAgentService.saveMessagesToSession(updatedMessages);
      // TRAC-203: capture startSequence so a browser closed mid-stream resumes
      // from the correct position.
      if (chunk.sequence !== undefined) {
        chatAgentService.updateStreamState(messageId, 0, true, chunk.sequence);
      }
      return updatedMessages;
    });
    return true;
  }

  if (chunk.type === "tool_progress") {
    setMessages((prev) => {
      const updatedMessages = prev.map((msg) =>
        msg.id === messageId
          ? patchTool(msg, chunk, { progress_message: chunk.status, status: "running" })
          : msg,
      );
      chatAgentService.saveMessagesToSession(updatedMessages);
      return updatedMessages;
    });
    return true;
  }

  if (chunk.type === "tool_use_complete") {
    if (chunk.toolName) {
      opts?.onToolComplete?.(chunk.toolName);
    }
    setMessages((prev) => {
      const updatedMessages = prev.map((msg) =>
        msg.id === messageId
          ? patchTool(msg, chunk, {
              status: "completed",
              parameters: chunk.parameters,
              result: chunk.result,
              result_summary: chunk.resultSummary,
            })
          : msg,
      );
      chatAgentService.saveMessagesToSession(updatedMessages);
      return updatedMessages;
    });
    return true;
  }

  if (chunk.type === "tool_error") {
    setMessages((prev) => {
      const updatedMessages = prev.map((msg) =>
        msg.id === messageId
          ? patchTool(msg, chunk, { status: "error", progress_message: chunk.error })
          : msg,
      );
      chatAgentService.saveMessagesToSession(updatedMessages);
      return updatedMessages;
    });
    return true;
  }

  return false;
}
